import { useCallback, useLayoutEffect, useRef, useState } from "react";

// One scrolling policy for all assistant conversations. No history/API changes.
export default function useConversationScroll(viewportRef, contentRef, messages, active, conversationKey) {
  const follow = useRef(true);
  const [showLatest, setShowLatest] = useState(false);
  const scrollToLatest = useCallback(() => {
    follow.current = true; setShowLatest(false);
    const viewport = viewportRef.current;
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [viewportRef]);

  useLayoutEffect(() => {
    follow.current = true; setShowLatest(false);
    if (active) scrollToLatest();
  }, [active, conversationKey, scrollToLatest]);

  useLayoutEffect(() => {
    if (active && follow.current) scrollToLatest();
  }, [messages, active, scrollToLatest]);

  useLayoutEffect(() => {
    if (!active) return;
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;
    const stick = () => { if (follow.current) viewport.scrollTop = viewport.scrollHeight; };
    const resize = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(stick);
    resize?.observe(content); resize?.observe(viewport);
    // Images/fonts/action cards may settle after the first render.
    content.addEventListener("load", stick, true);
    window.addEventListener("resize", stick);
    const frame = requestAnimationFrame(stick);
    return () => {
      resize?.disconnect(); cancelAnimationFrame(frame);
      content.removeEventListener("load", stick, true);
      window.removeEventListener("resize", stick);
    };
  }, [active, conversationKey, viewportRef, contentRef]);

  const onScroll = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const atEnd = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <= 48;
    follow.current = atEnd; setShowLatest(!atEnd);
  }, [viewportRef]);

  // Stop following immediately when the user scrolls up during a streamed reply.
  const onWheel = useCallback((event) => {
    if (event.deltaY < 0) { follow.current = false; setShowLatest(true); }
  }, []);
  return { onScroll, onWheel, showLatest, scrollToLatest };
}
